import React from 'react';
import _ from 'underscore';

import DataConnector from 'connect/DataConnector';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {Batch, Container, SampleDetails} from 'constants/SampleConstants';
import {BioentitySummary} from 'folder-items/FileConstants';
import {ManagedModelType} from 'models/ManagedModelDefinitions';
import {requestFetchManagedModel} from 'models/ManagedModelUtil';
import {makeSampleDetailsFromSamples} from 'samples/StageRunSampleModelManager';
import StorableStore from 'samples/StorableStore';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import APIUtil from 'util/APIUtil';

import LegacyAddSamplesModal, {AddSamplesModalProps, LegacySamplesSource} from './LegacyAddSamplesModal';

interface SourceMetadata {
  name: string;
  icon: BenchlingIconName;
  itemIds: Array<string>;
}

export interface AddSamplesFromIdsModalProps extends Omit<AddSamplesModalProps, 'sources'> {
  sourceMetadata: Array<SourceMetadata>;
}

interface EnhancedProps {
  sources: Array<LegacySamplesSource> | null;
}

export class AddSamplesFromIdsModalConnector extends DataConnector<
  AddSamplesFromIdsModalProps,
  EnhancedProps
> {
  stores = [StorableStore];

  renderLoading(): JSX.Element {
    return <AddSamplesFromIdsModal {...this.props} sources={null} />;
  }

  getOrFetchBioentitiesRequired(ids: Array<string>): Array<BioentitySummary> {
    const bioentityIds = ids.filter((id) => APIUtil.isBiologicalEntity(id));
    for (const id of bioentityIds) {
      requestFetchManagedModel(ManagedModelType.BIOENTITY_SUMMARY, id);
    }
    const summaries = bioentityIds.map((id) => this.reduxState().models.fileSummariesById[id]);
    return summaries.some((s) => s == null) ? this.fallBackToLoading() : summaries;
  }

  getOrFetchBatchesRequired(ids: Array<string>): Array<Batch> {
    const batchIds = ids.filter((id) => APIUtil.apiIdToMaybeType(id) === ModelTypeConstants.BATCH);
    for (const id of batchIds) {
      requestFetchManagedModel(ManagedModelType.BATCH, id);
    }
    const batches = batchIds.map((id) => this.storableStore().getBatch(id));
    return batches.some((b) => b == null) ? this.fallBackToLoading() : _.compact(batches);
  }

  getOrFetchContainersRequired(ids: Array<string>): Array<Container> {
    const containerIds = ids.filter((id) => APIUtil.isContainer(id));
    const containersById = this.getOrFetchMultipleOptionalDeprecated(
      ManagedModelType.CONTAINER,
      containerIds
    );
    const containers = containerIds.map((id) => containersById[id]);
    return containers.some((c) => c == null) ? this.fallBackToLoading() : containers;
  }

  containerToSampleDetails(container: Container): Array<SampleDetails> {
    const sampleDetails: Array<SampleDetails> = [];

    const batchIds = container.contents
      .map((content) => content.containedItemId)
      .filter((id) => APIUtil.isBatch(id));
    const batches = this.getOrFetchBatchesRequired(batchIds);
    for (const batch of batches) {
      sampleDetails.push(
        makeSampleDetailsFromSamples({
          batch,
          bioentitySummary: null,
          container,
          plate: null,
        })
      );
    }

    const bioentityIds = container.contents
      .map((content) => content.containedItemId)
      .filter((id) => APIUtil.isBiologicalEntity(id));
    const bioentities = this.getOrFetchBioentitiesRequired(bioentityIds);
    for (const bioentity of bioentities) {
      sampleDetails.push(
        makeSampleDetailsFromSamples({
          batch: null,
          bioentitySummary: bioentity,
          container,
          plate: null,
        })
      );
    }

    return sampleDetails;
  }

  /**
   * Fetch information about each sample given only container, batch, or bioentity ids.
   *
   * This function and this component do NOT do any validation of the items.
   * That is up to the caller that provided the sources and the itemIds in the first place.
   */
  fetchSampleDetails(itemIds: Array<string>): Array<SampleDetails> {
    const bioentities = this.getOrFetchBioentitiesRequired(itemIds);
    const batches = this.getOrFetchBatchesRequired(itemIds);
    const containers = this.getOrFetchContainersRequired(itemIds);

    const sampleDetails: Array<SampleDetails> = [];
    for (const bioentity of bioentities) {
      sampleDetails.push(
        makeSampleDetailsFromSamples({
          batch: null,
          bioentitySummary: bioentity,
          container: null,
          plate: null,
        })
      );
    }
    for (const batch of batches) {
      sampleDetails.push(
        makeSampleDetailsFromSamples({
          batch,
          bioentitySummary: null,
          container: null,
          plate: null,
        })
      );
    }
    for (const container of containers) {
      const containedSamples = this.containerToSampleDetails(container);
      sampleDetails.push(...containedSamples);
    }

    return sampleDetails;
  }

  enhanceProps(): EnhancedProps {
    const sources: Array<LegacySamplesSource> = [];

    for (const sourceInfo of this.props.sourceMetadata) {
      const sampleDetails = this.fetchSampleDetails(sourceInfo.itemIds);
      sources.push({
        name: sourceInfo.name,
        icon: sourceInfo.icon,
        sampleDetails,
      });
    }

    return {sources};
  }
}

export function AddSamplesFromIdsModal(props: AddSamplesFromIdsModalProps & EnhancedProps): JSX.Element {
  return <LegacyAddSamplesModal {...props} />;
}

export default AddSamplesFromIdsModalConnector.connect(AddSamplesFromIdsModal);
